import { useState } from "react";
import { useMutation } from "convex/react";
import { useRouterState } from "@tanstack/react-router";
import {
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Dialog,
  Field,
  Portal,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { LuMessageSquarePlus } from "react-icons/lu";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useEffectivePersonId } from "../impersonation";
import { readableError } from "@/lib/errors";
import { toastError, toastSuccess } from "./ui/toaster";

/**
 * "Send feedback", at the foot of the navigation on every page.
 *
 * For the first months of use: the quickest way to hear what is confusing is
 * to ask on the page where it happened. The page is recorded with the
 * message, so nobody has to describe where they were.
 */
export function FeedbackButton() {
  const submit = useMutation(api.appFeedback.submit);
  const effectivePersonId = useEffectivePersonId();
  const location = useRouterState({ select: (state) => state.location });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  async function send() {
    setSending(true);
    try {
      await submit({
        message,
        pagePath: location.href,
        effectivePersonId: effectivePersonId as Id<"persons"> | null,
      });
      toastSuccess("Thank you. Your feedback has been sent.");
      setMessage("");
      setOpen(false);
    } catch (error) {
      const { message: text, detail } = readableError(error, "Your feedback could not be sent");
      toastError(text, detail);
    } finally {
      setSending(false);
    }
  }

  return (
    <Box px={4} pb={2}>
      <Button size="sm" variant="ghost" w="full" justifyContent="flex-start" color="fg.muted" onClick={() => setOpen(true)}>
        <LuMessageSquarePlus />
        Send feedback
      </Button>

      <Dialog.Root open={open} onOpenChange={(event) => setOpen(event.open)} initialFocusEl={() => document.getElementById("feedback-message")}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="lg">
              <Dialog.Header>
                <Dialog.Title>Send feedback</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Field.Root>
                  <Field.Label>What would you like to tell us?</Field.Label>
                  <Textarea
                    id="feedback-message"
                    rows={6}
                    value={message}
                    placeholder="Something that confused you, did not work, or could be better."
                    onChange={(event) => setMessage(event.target.value)}
                  />
                  <Field.HelperText>
                    Sent with your name and the page you are on, so we can ask you about it.
                  </Field.HelperText>
                </Field.Root>
                <Text mt={3} fontSize="xs" color="fg.muted" truncate title={location.href}>
                  Page: {location.href}
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <ButtonGroup gap={2}>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button colorPalette="blue" loading={sending} disabled={message.trim() === ""} onClick={() => void send()}>
                    Send
                  </Button>
                </ButtonGroup>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
}
